import { useEffect } from 'react';

import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import CashlessConfirmForm from './confirmForm/CashlessConfirmForm';
import ConfirmForm from './confirmForm/ConfirmForm';
import messages from './ConfirmFundingView.messages';
import styles from './ConfirmFundingView.module.css';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../../tracking/pageView';
import { getDrawdownType } from '../../drawDown/getDrawdownType';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { Tooltip } from '../../ui/tooltip';
import { ACTIVATE_CONFIRM_STEP } from '../Funding.actions';
import FundingHeader from '../fundingHeader/FundingHeader';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import { FundingProgressIndicator } from '../fundingSteps/FundingProgessIndicator/FundingProgressIndicator';
import LoadingOverlay from '../fundingSteps/LoadingOverlay';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activateConfirm: (
        offerId: string,
        drawdownAmount: number,
        sweep?: number
    ) => {
        dispatch({
            type: ACTIVATE_CONFIRM_STEP,
            offerId,
            amount: drawdownAmount,
            sweep
        });
    }
});

const ConfirmFundingView = ({
    activateConfirm
}: ReturnType<typeof mapDispatchToProps>) => {
    useTrackPageView({ name: 'Confirmed Funding' });

    const stateKey = useStateKey();

    const location = getCurrentLocation();

    let drawdownAmount: string | undefined | number = getQueryParameter(
        location,
        'amount'
    );
    const sweepPercentage = getQueryParameter(location, 'sweep');
    const sweep = sweepPercentage ? parseFloat(sweepPercentage) : undefined;

    const offerId: string | undefined = getQueryParameter(location, 'offerId');

    useEffect(() => {
        if (drawdownAmount !== undefined && offerId) {
            activateConfirm(offerId, drawdownAmount as number, sweep);
        }
    }, [activateConfirm, offerId, drawdownAmount, sweep]);

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    const isCashlessDrawdown = drawdownAmount === '0';

    const { isLoading: loadingAccounts, data: accounts } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'Get Query Param drawdown amount is undefined',
            {
                tags: {
                    reporter: 'confirm_funding view'
                }
            }
        );
    }

    if (!offerId) {
        IwDataDogLogger.logError('Get Query Param offerId is undefined', {
            tags: {
                reporter: 'confirm_funding view'
            }
        });
    }

    if (loadingFormalOffer || loadingAccounts) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}>
                    <LoadingOverlay />
                </div>
            </MainSectionWrapper>
        );
    }

    drawdownAmount = parseFloat(drawdownAmount!);

    const hasAccountsData = accounts?.data;

    if (formalOffer && hasAccountsData) {
        const flexiApprovalTypes = ['flexi24', 'credit_facility'];

        const outstandingAccount = accounts.data!.accounts.find(
            (account) =>
                account.loan_status &&
                account.loan_status !== 'ended' &&
                (account.product_type === formalOffer.product_type ||
                    //topups can be any flexi type eg. Ongoing flexi24 can have credit_facility topup
                    (flexiApprovalTypes.includes(account.product_type!) &&
                        flexiApprovalTypes.includes(formalOffer.product_type!)))
        );
        const isTopUp = !!outstandingAccount;

        return (
            <>
                {getCountry() === 'UK' && <FundingProgressIndicator step={3} />}

                <FundingHeader
                    header={
                        <FormattedMessage
                            {...getHeaderMessage(formalOffer)}
                            values={{
                                tooltip: (
                                    <span>
                                        <Tooltip
                                            className={styles.tooltip}
                                            message={
                                                <FormattedMessage
                                                    {...messages.tooltip}
                                                />
                                            }
                                            placement="auto"
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    subHeader={
                        <FormattedMessage
                            {...getSubHeaderMessage(formalOffer)}
                        />
                    }
                    returnPath="/funding-requirements/"
                />
                {isCashlessDrawdown ? (
                    <CashlessConfirmForm formalOffer={formalOffer!} />
                ) : (
                    <ConfirmForm
                        drawdownAmount={drawdownAmount}
                        selectedInputAmount={drawdownAmount}
                        selectedSweepPercentage={sweep!}
                        offerId={offerId!}
                        isTopUp={isTopUp}
                        formalOffer={formalOffer}
                        outstandingAccount={outstandingAccount}
                    />
                )}
            </>
        );
    }
    return null;
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(ConfirmFundingView);

const getHeaderMessage = (formalOffer: TFormalOffer) => {
    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return messages.repaymentReductionHeader;
        case 'interest_only_extension':
            return messages.interestOnlyHeader;
        case 'revolving_credit_facility_first_drawdown':
            return messages.rcfFirstDrawdownHeader;
        case 'revolving_credit_facility_subsequent_drawdown':
            return messages.rcfSubsequentDrawdownHeader;
        case 'topup':
        default:
            return messages.header;
    }
};

const getSubHeaderMessage = (formalOffer: TFormalOffer) => {
    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return messages.repaymentReductionSubHeader;
        case 'interest_only_extension':
            return messages.interestOnlySubHeader;
        case 'revolving_credit_facility_first_drawdown':
            return messages.rcfFirstDrawdownSubHeader;
        case 'revolving_credit_facility_subsequent_drawdown':
            return messages.rcfSubsequentDrawdownSubHeader;
        case 'topup':
        default:
            return messages.subHeader;
    }
};
